import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import styles from './HealthSection.module.css';
import bgImage from '../assets/7.jpg';

const CircleLayer = ({ scrollYProgress, start, peak, end, color, floodColor, label, desc }) => {
  const scale = useTransform(scrollYProgress, [start, peak], [0.02, 6]);
  const ringOpacity = useTransform(scrollYProgress, [start, start + 0.05, peak - 0.03, peak], [0, 1, 1, 0]);
  const textOpacity = useTransform(scrollYProgress, [start + 0.05, start + 0.12, peak - 0.03, peak], [0, 1, 1, 0]);
  const textScale = useTransform(scrollYProgress, [start + 0.05, peak], [0.8, 1.2]);
  const floodOpacity = useTransform(scrollYProgress, [peak - 0.02, peak, end], [0, 1, 0]);

  return (
    <div className={styles.circleLayer}>
      <motion.div 
        className={styles.flood}
        style={{ backgroundColor: floodColor, opacity: floodOpacity }}
      />
      <div className={styles.centerContainer}>
        <motion.div 
          className={styles.ring}
          style={{ 
            borderColor: color, 
            backgroundColor: floodColor,
            scale: scale, 
            opacity: ringOpacity,
            x: "-50%",
            y: "-50%"
          }}
        />
        <motion.div 
          className={styles.textContent}
          style={{ 
            opacity: textOpacity, 
            scale: textScale,
            x: "-50%",
            y: "-50%" 
          }}
        >
          <h2 className={styles.label} style={{ color: color }}>
            {label}
          </h2>
          <p className={styles.desc}>
            {desc}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

const DesktopHealthScroll = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"]
  });

  return (
    <div ref={ref} className={styles.scrollContainer}>
      <div className={styles.stickyViewport}>
         <CircleLayer 
            scrollYProgress={scrollYProgress}
            start={0.00} peak={0.23} end={0.28}
            color="#4CAF50" floodColor="rgba(76,175,80,0.12)"
            label="Healthy"
            desc="Under 70 dB. Safe for extended exposure."
         />
         <CircleLayer 
            scrollYProgress={scrollYProgress}
            start={0.33} peak={0.56} end={0.61}
            color="#FF9800" floodColor="rgba(255,152,0,0.12)"
            label="Moderate"
            desc="70–85 dB. Use caution over time."
         />
         <CircleLayer 
            scrollYProgress={scrollYProgress}
            start={0.66} peak={0.89} end={0.94}
            color="#F44336" floodColor="rgba(244,67,54,0.12)"
            label="Harmful"
            desc="Above 85 dB. Protect your hearing."
         />
      </div>
    </div>
  );
};

const MobileHealthStatic = () => {
  const containerVars = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.12 }
    }
  };

  const itemVars = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <section className={styles.sectionMobile}>
      <div 
        className={styles.bgMobile}
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className={styles.overlayMobile} />

      <div className={styles.containerMobile}>
        <motion.div 
          className={styles.textContentMobile}
          variants={containerVars}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.div variants={itemVars} className={styles.eyebrowMobile}>
            YOUR HEALTH
          </motion.div>
          <motion.h2 variants={itemVars} className={styles.titleMobile}>
            Some places are safe.<br/>Know which.
          </motion.h2>
          <motion.p variants={itemVars} className={styles.bodyMobile}>
            Noise Pad color-codes every reading. Green for healthy, amber for moderate, red for harmful. Wherever you go, you instantly know if it's a place to linger or leave.
          </motion.p>
          
          <motion.div variants={itemVars} className={styles.badges}>
            <div className={`${styles.badge} ${styles.badgeGreen}`}>
              <span className={styles.dot}>🟢</span> Healthy
            </div>
            <div className={styles.badgeSeparator}>&middot;</div>
            <div className={`${styles.badge} ${styles.badgeAmber}`}>
              <span className={styles.dot}>🟡</span> Moderate
            </div>
            <div className={styles.badgeSeparator}>&middot;</div>
            <div className={`${styles.badge} ${styles.badgeRed}`}>
              <span className={styles.dot}>🔴</span> Harmful
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const HealthSection = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <MobileHealthStatic /> : <DesktopHealthScroll />;
};

export default HealthSection;
